<template>
  <b-form-group class="mb-0"  :label="label">
    <template v-slot:label v-if="icon">
      {{label}}
      <b-button
          data-test-path="text-pills-icon-button"
          @click="clickOnIcon"
          style="width: 30px; height: 30px;"
          class="ml-2"
          variant="plm-action">
        <font-awesome-icon style="position: absolute; margin-left: -7px; margin-top: -8px" :icon="icon"></font-awesome-icon>
      </b-button>

    </template>

  <b-container ref="container" :class="'p-0 m-0 ' + (!readOnly ? 'tp-text-pills-root ':'tp-text-pills-root-ro ') + ((glowing && !readOnly ) ? 'tp-glow' : '')"
               :tabindex="readOnly ? '':'0'" fluid
               @focusout="focusOut" @focus="focusIn" @click="focusIn">
    <b-row  class="p-0 m-0 tp-text-pills-row" :style="readOnly ? 'background-color: GhostWhite !important' : ''" align-v="center" ref="row">
      <b-col cols="auto"
             :class="'d-flex align-items-center ml-1 pl-2 m-0 text-left align-middle text-pills-pill border rounded-lg rounded-pill border-' + pill.variant + (readOnly ? ' pr-2' : ' pr-1')"
             v-for="pill in pills" :key="pill.id">
        <b-btn  v-if="!readOnly"
                variant="plm-toolbar-secondary" class="tp-text-pills-remove-btn border-0" @mousedown.stop="" @click.stop="editItem(pill.id)">
          <font-awesome-icon :icon="['far', 'pencil']" transform="shrink-4"></font-awesome-icon>
        </b-btn>
          <span :tabindex="readOnly ? '-1':'0'"
                ref="pillText"
                class="tp-text-pill"
                @click.stop=""
                @mousedown.stop=""
                @mouseup.stop="pillMouseUp"
                style="margin-bottom: 2px"
          >{{ pill.text }}</span>
          <b-btn  v-if="!readOnly"  variant="plm-toolbar-danger " class="tp-text-pills-remove-btn border-0" style="font-family: serif" @mousedown.stop="" @click.stop="removeItem(pill.id)">×</b-btn>
      </b-col>

      <b-col v-if="!readOnly" cols="auto" class="pl-2 m-0 text-left align-middle">
        <b-form-input
            ref="input"
            v-model="inputText"
            class="tp-text-pills-input"
            :placeholder="placeholder"
            @input="inputChange"
            @keypress="inputChange"
            @keydown="onKeyUp"
            @focusout="inputFocusOut($event)"
            @click="glowing=true"
        />
      </b-col>
    </b-row>
  </b-container>
    <b-container >
          <b-row>
            <b-col  class="pl-0 pr-0 pt-1">
              <small v-if="!valid2"
                     class="form-text text-danger">{{errorMessage || '&nbsp;'}}
              </small>
            </b-col>
          </b-row>
    </b-container>
  </b-form-group>

</template>

<script>
import {getTextWidth, setCaretPosition} from "@/utils/TextMetrics";
import {nextTick} from "@/utils/Executors";
import ValidatedField from "@/validation/ValidatedField";

export default {
  name: "TextPillsList",
  created() {
    this.ctr = 0;
  },
  mounted() {
    this.refreshPills();
  },
  methods:{
    /**
     * @param {FocusEvent} event
     */
    focusOut(event){
      if(event.relatedTarget && this.$el.contains(event.relatedTarget)){
        return;
      }
      this.glowing = false;
    },

    focusIn(){
      if(!this.readOnly) {
        this.glowing = true;
        this.$refs['input'].focus();
      }
    },
    focus(){
      if(!this.readOnly) {
        this.glowing = true;
        this.$refs['input'].focus();
      }
    },

    inputFocusOut(event){
      if(event.relatedTarget && this.$el.contains(event.relatedTarget) && event.relatedTarget.classList.contains("text-pills-remove-btn")){
        return;
      }

      if(this.inputText.trim() != ''){
        this.parse(this.inputText.length, false);
      }
    },

    parse(delimiter, setCaret){
      delimiter = delimiter ?? this.inputText.length;
      let text = this.inputText.substring(0, delimiter);
      let parts = text.split(this.separators);
      parts.forEach(part => {
        part = part.trim();
        if(part){
          let variant = part?.match(this.pattern) ? "primary" : "warning";
          this.pills.push({text:part, id:this.ctr++, variant});
        }
      });
      this.inputText = this.inputText.substring(delimiter);
      this.inputChange(null, setCaret);
      if(setCaret) {
        nextTick(() => {
          setCaretPosition(this.$refs.input, 0);
        });
      }
      this.changeModel();
    },

    changeModel(){
      let newItems = this.pills.map(pill => pill.text);
      this.$emit('model-change', newItems);
    },

    pillMouseUp(){
      if(this.$refs.pillText?.setSelectionRange){
        if(this.$refs.pillText.selectionStart !== this.$refs.pillText.selectionEnd){
          return;
        }
      }
      let docSelection = document.getSelection();
      if(docSelection && docSelection.toString()){
        return;
      }

      if(!this.readOnly){
        this.$refs['input'].focus();
      }
    },

    editItem(pillId){
      let selection = this.pills.filter(item => item.id === pillId);
      this.parse(null, false);
      this.pills = this.pills.filter(item => item.id !== pillId);
      this.glowing=true;
      this.inputText = selection[0].text + this.inputText;

      this.$refs['input'].focus();
      setCaretPosition(this.$refs['input'], 0);
      this.inputChange();
      this.changeModel();
    },

    removeItem(pillId){
      this.pills = this.pills.filter(item => item.id !== pillId);
      this.changeModel();
      this.glowing=true;
      this.$refs['input'].focus();
      this.checkTextValidity();
    },

    onKeyUp(ev){
      if(this.readOnly){
        return;
      }
      let key = ev.keyCode || ev.charCode;

      if( key == 8 || key == 46 ) {
        return this.inputChange("Backspace");
      }
      if( key == 27 ) { //escape
        document.activeElement?.blur();
      }
      if( key == 46 ) {
        return this.inputChange("Del");
      }
      if(key == 13 || ev.key.match(this.separators)){ // ENTER
        nextTick(() =>
          this.parse(null, false));
      }
    },

    inputChange(key, setCaret){
      if(this.readOnly){
        return;
      }
      setCaret = setCaret ?? true;
      if(key === "Backspace"){
        if(this.$refs.input.selectionStart === 0 && this.pills) {
          nextTick(() => {
            let pill = this.pills.pop();
            if(pill){
              this.inputText = pill.text + this.inputText;
              this.inputChange();
              this.changeModel();
              if(setCaret){
                nextTick(() => {
                  setCaretPosition(this.$refs.input, pill.text.length);
                });
              }
            }
            this.checkTextValidity();
            return false;
          });
          this.checkTextValidity();
          return false;
        }
      }
      if(this.$refs.input) {
        let width = getTextWidth(this.inputText, this.$refs.input);
        let maxWidth = this.$refs.row.clientWidth - 30;
        if (width > maxWidth) {
          width = maxWidth;
        }

        width += 10;
        this.inputWidth = width;
      }
      this.checkTextValidity();
    },

    refreshPills(){
      if(this.items === null){
        this.pills = [];
        return
      }
      this.pills = this.items.map((text, id)=>{
        let variant =  text?.match(this.pattern) ? "primary" : "warning";
        return {text, id:this.ctr++, variant};
      });

      this.checkTextValidity();
    },

    checkTextValidity(){
      let valid = true;
      this.pills.forEach(pill=>{
        let textValid = pill.text?.match(this.pattern);
        if(!textValid){
          valid = false;
        }
      });

      this.itemsValid = valid;
      this.textValid = this.inputText == "" || this.inputText.match(this.pattern);
      if(!this.textValid){
        valid = false;
      }
      this.$emit('update:valid', valid);
    },

    clickOnIcon(){
      this.$emit('icon-clicked');
    }

  },
  watch:{
    items:function(){
      this.refreshPills();
    },
    pattern:function(){
      this.refreshPills();
    },
  },
  model: {
    prop: 'items',
    event: 'model-change',

    /* sync back props */
    valid: {type: Boolean, default:true},
  },
  props:{
    iconCliked:    Function,

    placeholder:    {type: String, default: ""},
    valid2:         {type: Boolean, default:true},
    errorMessage:   {type: String, default: ""},
    readOnly:       {type:Boolean, default: false},
    separators:     {type:RegExp, default: () => / |;|,|\\t/},
    pattern:        {type: RegExp, default: () => /.*/ },
    items:          {type: Array, default:()=>[]},
    label:          {type: String, default: ""},
    hint:           {type: String, default: ""},
    icon:           {type: String, default: ""},
  },
  data: () => {
    return {
      pills:[],
      inputText: "",
      inputWidth: 30,
      glowing: false,
      itemsValid:true,
      textValid:true
   }
  }
}
</script>ActionsActions